<template>
    <div class="cost-table" :class="{ 'table-on-modal': componentOnModal, 'overlay-sc': overlayScrollb }">
        <FilterBar 
            v-if="filterBar"
            :cookieName="(componentOnModal) ? modalTempCookie : 'CostFilterOptionsVue2'" 
            :class="{ 'no-totals': !showTotals }"
            :skip-cookies="modalTempCookie"
            filterBarType="costs" 
            @change="filtersChanged" 
            :mainFilters="mainFilters"
            :secondaryFilters="secondaryFilters"
            ref="costs-table-filterbar"
        >
            <template v-if="showTotals" v-slot:right-bar>
                <TotalAmountsCost :num-format="'euro'" />
            </template>
        </FilterBar>

        <div class="content--inner">
            <overlay-scrollbars :options="scrollBarsOptions" class="cost-table-scrollbar">
                <b-table
                    primary-key="id"
                    :class="['costs-table gk-table gk-table--responsive gk-table--striped gk-table--hover', { '': componentOnModal, 'gk-table--statusmark': !selectableRows, 'gk-table--headed': selectableRows }]"
                    :items="costList"
                    :fields="tableFields"
                    :tbody-tr-class="rowClass"
                    :key="tableKey"
                    @row-clicked="rowClicked"
                    :busy="loading"
                    show-empty
                    table-busy
                >
                    <template #empty="">
                        <EmptyState :class="{ 'emptystate-simple': componentOnModal }">
                            <template v-if="!componentOnModal" v-slot:image>
                                <img src="/static/img/empty-states/empty-state-cost.png">
                            </template>
                            <template v-slot:title>
                                {{ $gettext('We couldn\'t find any costs') }}
                            </template>
                            <template v-slot:content>
                                {{ $gettext('Start by creating a new one or try updating your filters.') }}
                            </template>
                            <template v-if="!componentOnModal" v-slot:cta>
                                <GekkoButton 
                                    :text="$gettext('Add a new cost')"
                                    :to="{ name: 'AddCost' }"
                                    icon="fa-solid fa-plus"
                                />
                            </template>
                        </EmptyState>
                    </template>
                    <template #table-busy="">
                        <LoadingSpinner min-height />
                    </template>
                    
                    <template v-slot:head(selector)="">
                        <b-form-checkbox class="cost-row-checkbox" v-model="allItemsSelected" @change="selectAllTableItems" />
                    </template>

                    <template v-if="selectableRows" #cell(selector)="row">
                        <b-form-checkbox class="cost-row-checkbox" v-model="row.item.selected" @change="selectedTableItem" />
                    </template>

                    <template #cell(date)="data">
                        <div class="row-status-indicator"></div>
                        <span class="align-dates">{{data.value | formatDate}}</span>
                    </template>
                    <template #cell(is_invoiced)="data">
                        <div v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="$gettext('This cost has already been invoiced.')" class="d-flex justify-content-center">
                            <font-awesome-icon v-if="data.item.is_invoiced" icon="fa-solid fa-file-lines" />
                        </div>
                    </template>
                    <template #cell(cost_type)="data">
                        <div class="d-flex justify-content-center"><span v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="get_costtype_text(data.value)" v-html="get_icon(data.value)" /></div>
                    </template>
                    <template #cell(description)="data">
                        <span v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="data.value" class="coldesc coldesc--md">{{data.value}}</span>
                    </template>
                    <template #cell(customer)="data">
                        <div class="d-block" v-if="data.item.customer"><strong>{{showCustomer(data.item.customer)}}</strong></div>
                        <span v-if="data.item.project">{{showProject(data.item.project)}}</span>
                    </template>
                    <template #cell(supplier)="data">
                        <div class="d-block" v-if="data.item.supplier">{{showCustomer(data.item.supplier)}}</div>
                    </template>
                    <template #cell(amount)="data">
                        <div class="text-right">€ {{data.value | formatNumber}}</div>
                    </template>
                    <template #cell(completed)="data">
                        <div class="status-cell">
                            <div class="d-flex">
                                <div class="status-circle-badge badge-blue">
                                    <span v-if="data.item.is_verified" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="$gettext('This cost is verified.')">
                                        <font-awesome-icon icon="fa-solid fa-badge-check" />
                                    </span>
                                </div>
                                <div class="status-circle-badge badge-green">
                                    <span v-if="data.item.image" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="$gettext('Cost proof is present')">
                                        <font-awesome-icon icon="fa-solid fa-paperclip" />
                                    </span>
                                </div>
                                <div class="status-circle-badge badge-blue">
                                    <span v-if="data.item.is_uploaded" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="$gettext('Created via CSV file upload')">
                                        <font-awesome-icon icon="fa-solid fa-cloud-upload" />
                                    </span>
                                </div>
                                <div class="status-circle-badge badge-blue">
                                    <span v-if="data.item.transactions.length" v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" :title="$gettext('Matched with a bank transaction')">
                                        <font-awesome-icon icon="fa-solid fa-university" />
                                    </span>
                                </div>
                            </div>
                            <div :class="'status-badge status-'+costStatus(data.item)">
                                {{complete(data.item)}}
                            </div> 
                        </div>
                    </template>
                    <template #cell(created_by)="data">
                        <div class="d-flex justify-content-center"><Avatar :user-id="getAvatarId(data.value)"></Avatar></div>
                    </template>

                    <template slot="bottom-row" v-if="nextUrl">
                        <td :colspan="fields.length" class="table-load-more">
                            <GekkoButton 
                                v-if="nextUrl && !fetchingData" 
                                @click="handleScroll()"
                                id="load_more_button" 
                                class="text-info"
                                variant="link" 
                                :text="$gettext('Load more costs')" 
                            />
                            <LoadingSpinner v-if="fetchingData" />
                        </td>
                    </template>
                </b-table>
            </overlay-scrollbars>
        </div>
    </div>
</template>
<script>
    import { mapGetters } from 'vuex';

    import Avatar from "@/components/Avatar"
    import FilterBar from "@/components/list_filters/FilterBar"
    import GekkoButton from "@/components/GekkoButton"
    import CostService from "@/store/services/cost.service.js"
    import CostTypeService from "@/store/services/costtype.service.js"
    import TotalAmountsCost from "@/components/costs/TotalAmountsCost"
    import EmptyState from "@/components/ui/EmptyState"
    import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
    import CustomerService from '@/store/services/customer.service'
    import ProjectService from '@/store/services/project.service'

    export default {
        name: "CostTable",
        components: { GekkoButton, 
            Avatar, 
            FilterBar, 
            TotalAmountsCost, 
            EmptyState,
            LoadingSpinner
        },
        props: {
            data_object: Object,
            overlayScrollb: {
                type: Boolean,
                default: true
            }, 
            showTotals: {
                type: Boolean,
                default: true
            },
            componentOnModal: {
                type: Boolean,
                default: false
            },
            modalTempCookie: {
                type: String,
                default: null
            },
            selectableRows: {
                type: Boolean,
                default: false
            },
            checkedRows: {
                type: Array,
                default: () => []
            },
            selectedReturn: {
                type: String,
                default: 'id'
            },
            mainFilters: {
                type: Array,
                default: () => ['SearchFilter', 'DateRangeFilter']
            },
            secondaryFilters: {
                type: Array,
                default: () => ['CostCustomerFilter', 'UserFilter', 'CostSupplierFilter', 'CostTransactionFilter', 'CostTypeFilter', 'VatFilter', 'CostStatusFilter', 'CostVerifiedFilter']
            },
            extended: {
                type: Boolean,
                default: false
            },
            excludeTableFields: {
                type: Array,
                default: () => []
            },
            filterBar: {
                type: Boolean,
                default: true
            },
        },
        data () {
            return {
                transProps: {
                    name: 'flip-list'
                },
                filters: {},
                fields: [
                    { key: 'date', label: this.$gettext('Date'), sortable: false, tdClass: 'col-date width-125' },
                    { key: 'is_invoiced', label: this.$gettext('Invoiced'), sortable: false, tdClass: 'col-type' },
                    { key: 'cost_type', label: this.$gettext('Type'), sortable: false, tdClass: 'col-type' },
                    { key: 'description', label: this.$gettext('Description'), sortable: false, tdClass: 'col-description d-none d-md-table-cell', thClass: 'd-none d-md-table-cell' },
                    { key: 'customer', label: this.$gettext('Customer'), sortable: false, tdClass: 'col-costs-customer' },
                    { key: 'supplier', label: this.$gettext('Supplier'), sortable: false, tdClass: 'col-costs-supplier' },
                    { key: 'amount', label: this.$gettext('Amount'), sortable: false, tdClass: 'col-amount', thClass: 'text-right' },
                    { key: 'completed', label: this.$gettext('Status'), sortable: false, tdClass: 'col-status', thClass: 'text-center' },
                    { key: 'created_by', label: this.$gettext('User'), sortable: false, tdClass: 'col-avatar' },
                ],
                costList: [],
                costTypes: {},
                customerList: [],
                projectList: [],
                nextUrl: '',
                totalNumber: 0,
                fetchingData: false,
                tableKey: 0,
                loading: true,
                scrollBarsOptions: {
                    callbacks : {
                        onScrollStop: this.handleScroll
                    }
                },
                allItemsSelected: false
            }
        },
        computed: {
            ...mapGetters({ 
                getCompanyId: 'main/getCompanyId'
            }),
            selectedTableItems () {
                let selected = null
                if (this.costList) {
                    if (this.selectedReturn === 'object') {
                        selected =  this.costList.filter(item => item.selected)
                    } else {
                        selected =  this.costList.filter(item => item.selected).map((item) => item.id)
                    }
                }
                return selected
            },
            tableFields () {
                return this.fields.filter(field => !this.excludeTableFields.includes(field.key));
            }
        },
        watch: {
            costList(newValue) {
                this.$store.commit('costs/SET_TEMP_COST_LIST', newValue);
            },
            nextUrl (newValue) {
                this.$store.commit('costs/SET_TEMP_COST_LIST_NEXT', newValue);
            }
        },
        created(){
            if (this.selectableRows) {
                this.fields.unshift({ key: 'selector', label: '' })
            }
            window.addEventListener('scroll', this.handleScroll);
            CostTypeService.list({ company: this.getCompanyId }).then((response) => {
                for (var i = 0; i < response.data.length; i++) {
                    this.costTypes[response.data[i].id] = response.data[i];
                }
            });
            this.$store.commit("filterStore/SET_OWNER_COMPANY", this.getCompanyId);

            this.unsubscribe = this.$store.subscribe((mutation) => {
                if (mutation.type === 'costs/REFRESH_COST_LIST') {
                    this.refreshData()
                }
            })
          CustomerService.list()
            .then((response) => {
              this.customerList = response.data
            })
          ProjectService.list()
            .then((response) => {
              this.projectList = response.data
            })
        },
        mounted () {
            this.$emit('mounted', false)
        },
        destroyed () {
            this.unsubscribe()
            window.removeEventListener('scroll', this.handleScroll);
            if (this.componentOnModal) {
                this.$emit('destroyed')
            }
        },
        methods: {
            getCosts(filters){
                this.allItemsSelected = false;
                this.loading = true;
                this.fetchingData = true;
                filters.owner_company = this.getCompanyId;
                this.filters = filters;

                if (this.extended) filters.extended = true

                CostService.list(filters).then((response) => {
                    this.costList = response.data.results;
                    this.nextUrl = response.data.next;
                    this.totalNumber = response.data.count;
                    this.loading = false;
                    this.fetchingData = false;
                    if (this.checkedRows) this.checkRows()
                    if (this.selectableRows) this.$emit('get-costs', response.data.count)
                })
            },
            rowClicked (item) {
                if (!this.componentOnModal) {
                    this.$store.commit('costs/setCostToEdit', item);
                    this.$router.push({ name: 'EditCost', params: { costId: item.id } });
                } else {
                    if (this.selectableRows) {
                        const cost_index = this.costList.findIndex(cost => cost.id === item.id)
                        this.$set(this.costList, cost_index, { ...this.costList[cost_index], selected: !item.selected })
                        this.selectedTableItem()
                    } else {
                        this.$emit('row-clicked', item)
                    }
                }
            },
            refreshData () {
                this.$refs['costs-table-filterbar'].filtersChanged()
            },
            selectAllTableItems () {
                this.costList.forEach((cost, cost_index) => {
                    this.$set(this.costList, cost_index, { ...cost, selected: this.allItemsSelected })
                });
                this.selectedTableItem()
            },
            selectedTableItem () {
                this.costList.splice() // ugly hack for triggering computed property update
                this.$emit('selected-item', this.selectedTableItems)
            },
            complete(item){
                var status = this.costStatus(item);
                if (status === 'complete') {
                    return this.$gettext('Complete');
                }
                if (status === 'incomplete') {
                    return this.$gettext('Incomplete');
                }
                if (status === 'notpaid') {
                    return this.$gettext('Unpaid');
                }
            },
            get_icon(icon) {
                let icon_id = (this.extended) ? icon.id : icon
                if (icon_id && this.costTypes[icon_id]) {
                    icon_id = this.costTypes[icon_id].icon;
                    return "&#x" + icon_id + ";";
                }
                return '';
            },
            get_costtype_text(costtype){
                let costtype_id = (this.extended) ? costtype.id : costtype

                if (costtype_id && this.costTypes[costtype_id]) {
                    costtype_id = this.costTypes[costtype_id].name;
                    return costtype_id;
                }
                return '';
            },
            showCustomer (customer) {
              const customer_id = (this.extended) ? customer.id : customer
              const c = this.customerList.find(item => item.id === customer_id)
              return c ? c.name : ''
            },
            getAvatarId (avatar) {
                const avatar_id = (this.extended) ? avatar.id : avatar
                return avatar_id
            },
            showProject(id){
              const project = this.projectList.find(item => item.id === id)
              return project ? project.name : ''
            },
            filtersChanged(value){
                this.getCosts(value);
            },
            costStatus(item){
                if (item.payment_type === "notpaid") {
                    return 'notpaid';
                }
                if (item.missing_attributes.length === 0 || item.marked_as_completed) {
                    return 'complete';
                }
                return 'incomplete';
            },
            rowClass(item){
                let row_class = ''
                if (item) {
                    let status = this.costStatus(item);
                    if (status === 'notpaid') {
                        row_class = 'costs-table-row cost-notpaid'
                    } else if (status === 'complete') {
                        row_class = 'costs-table-row cost-complete';
                    } else {
                        row_class = 'costs-table-row cost-incomplete';
                    }
                    if (item.selected) row_class += ' item-selected'
                }
                return row_class
            },
            isInViewport(element_id) {
                var element = document.getElementById(element_id)
                var rect = element.getBoundingClientRect();
                var html = document.documentElement;
                return (
                    rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= (window.innerHeight || html.clientHeight) &&
                    rect.right <= (window.innerWidth || html.clientWidth)
                );
            },
            handleScroll(){
                if (!this.fetchingData && this.nextUrl) {
                    var loadNewCosts = this.isInViewport('load_more_button');
                    if (loadNewCosts) {
                        this.fetchingData = true;
                        CostService.loadNextPage(this.nextUrl).then((response) => {
                            this.fetchingData = false;
                            this.nextUrl = response.data.next;
                            this.costList = this.costList.concat(response.data.results);
                            if (this.checkedRows) this.checkRows()
                            if (this.selectableRows) this.$emit('get-costs', response.data.count)
                        })
                    }
                }
            },
            checkRows () {
                for (var i = 0; i < this.costList.length; i++) {
                    if (this.checkedRows.includes(this.costList[i].id)) {
                        this.$set(this.costList, i, { ...this.costList[i], selected: true })
                    }
                }
            }
        }
    };
</script>
<style lang="scss">
    .cost-table {
        .os-host.cost-table-scrollbar {
            min-height: 360px;
        }
    }
    .cost-table.table-on-modal {
        &.overlay-sb {
            .os-host.cost-table-scrollbar {
                height: 300px !important;
                min-height: 300px;
                max-height: 300px;
                .os-content { padding: 0 !important; }
            }
        }
    }
    table.table-transition .flip-list-move {
        transition: transform 0.3s;
    }

    .col-date        { width: 0%; white-space: nowrap; }
    .col-type        { width: 5%; }
    .col-description { width: 25%; min-width: 150px; font-size: 14px; }
    .col-costs-customer    { width: 18%; }
    .col-costs-supplier    { width: 17%; }
    .col-amount      { width: 125px; min-width: 125px; white-space: nowrap; font-size: 18px; }
    .col-status      { width: 220px; min-width: 220px; white-space: nowrap; }
    .col-avatar      { width: 0%; }

    @media (min-width: '992px') {
        .col-description { width: 30%; min-width: 200px; }
        .col-status      { width: 220px; min-width: 220px; }
    }

    .col-customer {
        > * { line-height: 1; }
        font-size: 16px;
        span { font-size: 14px; color: #6b7280; }
    }

    .costs-table-row {
        height: 52px;
        cursor: pointer;
        td { padding: 20px; }
    }

    .status-cell {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
</style>
<style lang="scss" scoped>
    .clickable-link {
        color: $colorBlue;
        cursor: pointer;
        float: right;
        font-size: 14px;
        &:hover {
            color: darken($colorBlue, 20%);
        }
    }
    .filters-totals-wrap {
        position: sticky;
        top: 0;
        z-index: 100;
        background: white;
    }
</style>
